import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

/**
 * Feature card with full-bleed background image, gradient overlay, and content.
 * Zigzag layout: use align="left" and align="right" on alternating sections.
 */
const FeatureCard = ({
  tag,
  tagIcon: TagIcon,
  title,
  description,
  features = [],
  buttonText,
  backgroundImage,
  index = 0,
  align = 'left',
}) => {
  const isRight = align === 'right';
  const normalizedFeatures = features.map((f) =>
    typeof f === 'string' ? { text: f, icon: null } : { text: f.text, icon: f.icon }
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="relative min-h-[360px] md:min-h-[420px] rounded-[32px] overflow-hidden group"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Gradient: dark on content side for readability (lighter overlay so images show through more) */}
      <div
        className={`absolute inset-0 z-10 ${
          isRight
            ? 'bg-gradient-to-l from-black/65 via-black/35 to-transparent'
            : 'bg-gradient-to-r from-black/65 via-black/35 to-transparent'
        }`}
      />
      {/* Lighter overlay on mobile so text stays readable but image visible */}
      <div className="absolute inset-0 z-10 bg-black/55 md:bg-transparent md:opacity-0 pointer-events-none" aria-hidden />

      {/* Content — left or right aligned for zigzag */}
      <div
        className={`relative z-20 h-full flex flex-col justify-center p-8 md:p-12 max-w-lg ${
          isRight ? 'ml-auto items-end text-right' : 'items-start'
        }`}
      >
        {/* Tag */}
        <div className={`flex items-center gap-2 mb-4 ${isRight ? 'flex-row-reverse' : ''}`}>
          {TagIcon ? (
            <span className="w-7 h-7 rounded-lg bg-[#3B82F6]/15 border border-[#3B82F6]/30 flex items-center justify-center shrink-0">
              <TagIcon className="w-3.5 h-3.5 text-[#60A5FA]" strokeWidth={2} />
            </span>
          ) : (
            <span className="h-2 w-2 rounded-full bg-[#3B82F6] shrink-0" />
          )}
          <span className="text-[#3B82F6] text-sm font-medium tracking-wide">{tag}</span>
        </div>

        <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
          {title}
        </h2>

        <p className={`text-gray-300 text-sm md:text-base leading-relaxed mb-6 max-w-md ${isRight ? 'pl-2' : 'pr-2'}`}>
          {description}
        </p>

        <ul className={`space-y-2.5 mb-8 ${isRight ? 'items-end' : ''}`}>
          {normalizedFeatures.map((item, i) => {
            const Icon = item.icon;
            return (
              <li
                key={i}
                className={`flex items-center text-gray-200 text-sm ${isRight ? 'flex-row-reverse' : ''}`}
              >
                {Icon ? (
                  <span className={`w-8 h-8 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/25 flex items-center justify-center shrink-0 ${isRight ? 'ml-3' : 'mr-3'}`}>
                    <Icon className="w-4 h-4 text-[#60A5FA]" strokeWidth={2} />
                  </span>
                ) : (
                  <span className={`h-5 w-5 rounded-full border border-[#3B82F6]/50 flex items-center justify-center bg-[#3B82F6]/10 shrink-0 ${isRight ? 'ml-3' : 'mr-3'}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#60A5FA]" />
                  </span>
                )}
                {item.text}
              </li>
            );
          })}
        </ul>

        {buttonText && (
          <button
            type="button"
            className={`flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors text-white px-5 py-2.5 rounded-full w-fit text-sm font-medium backdrop-blur-sm border border-white/10 ${isRight ? 'flex-row-reverse' : ''}`}
          >
            <span>{buttonText}</span>
            <ChevronRight size={16} className={`text-gray-300 ${isRight ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default FeatureCard;
